import React, { Children, memo } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, Activity, Heart, ArrowRight, ExternalLink, Instagram, Mail, Phone } from 'lucide-react';
import { HamburgerMenu } from '../components/HamburgerMenu';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function MemoryCareTogether() {
  const scrollToEvents = () => {
    const eventsSection = document.getElementById('events');
    if (eventsSection) {
      eventsSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-[#FAF6F0] text-[#2C1810] font-inter selection:bg-[#A0522D] selection:text-white" data-id="element-217">
      <HamburgerMenu data-id="element-218" />
      {/* 1. HERO SECTION */}
      <header className="relative pt-24 pb-20 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto flex flex-col items-center text-center" data-id="element-219">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-4xl mx-auto flex flex-col items-center" data-id="element-220">
          <motion.div variants={fadeInUp} className="mb-6 flex items-center gap-3" data-id="element-221">
            <div className="h-px w-8 bg-[#8B6914]" data-id="element-222"></div>
            <span className="text-[#8B6914] font-medium tracking-widest text-xs md:text-sm uppercase" data-id="element-223">
              Grounded Coffee · Third Anniversary
            </span>
            <div className="h-px w-8 bg-[#8B6914]" data-id="element-224"></div>
          </motion.div>

          <motion.h1 variants={fadeInUp} className="font-fraunces text-5xl md:text-7xl lg:text-8xl leading-[1.1] text-[#2C1810] mb-6" data-id="element-225">
            Memory Care <span className="italic text-[#A0522D]" data-id="element-226">Together</span>
          </motion.h1>

          <motion.p variants={fadeInUp} className="text-lg md:text-xl font-medium text-[#8B6914] mb-8" data-id="element-227">
            June 2026 · Livermore, CA
          </motion.p>

          <motion.p variants={fadeInUp} className="text-base md:text-lg text-[#2C1810]/80 max-w-2xl leading-relaxed mb-10" data-id="element-228">
            June is Alzheimer's & Brain Awareness Month. This year, we're
            dedicating our entire third anniversary to the families, caregivers,
            and neighbors navigating memory diseases. Memory Care Together is a
            month of community events — talks, a fundraiser, a 50-mile
            challenge, and more — right here in Livermore. Because these
            conversations are too important to have behind closed doors.
          </motion.p>

          <motion.button variants={fadeInUp} onClick={scrollToEvents} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} className="bg-[#A0522D] text-white px-8 py-4 rounded-full font-medium text-lg tracking-wide hover:bg-[#8B4513] transition-colors flex items-center gap-2 shadow-md shadow-[#A0522D]/20" data-id="element-229">
            See the Events
            <ArrowRight size={20} data-id="element-230" />
          </motion.button>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.4
      }} className="mt-20 w-full max-w-5xl aspect-[16/9] md:aspect-[21/9] rounded-3xl overflow-hidden shadow-lg" data-id="element-231">
          <img src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?q=80&w=2689&auto=format&fit=crop" alt="People gathering and talking warmly over coffee" className="w-full h-full object-cover sepia-[0.2] contrast-[0.95]" data-id="element-232" />
        </motion.div>
      </header>

      {/* 2. WHY THIS MATTERS SECTION */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-[#F5EFE6]" data-id="element-233">
        <div className="max-w-5xl mx-auto" data-id="element-234">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center" data-id="element-235">
            <div className="md:col-span-5 flex flex-col gap-8 justify-center md:justify-start" data-id="element-236">
              <motion.div variants={fadeInUp} className="text-center md:text-left" data-id="element-237">
                <span className="block font-fraunces text-5xl md:text-7xl text-[#A0522D] mb-2" data-id="element-238">
                  Every 65s
                </span>
                <span className="text-sm font-medium text-[#2C1810]/70 uppercase tracking-wider" data-id="element-239">
                  Someone is Diagnosed
                </span>
              </motion.div>
              <div className="hidden md:block w-16 h-px bg-[#8B6914]/30" data-id="element-240"></div>
              <motion.div variants={fadeInUp} className="text-center md:text-left" data-id="element-241">
                <span className="block font-fraunces text-5xl md:text-7xl text-[#A0522D] mb-2" data-id="element-242">
                  7.4M
                </span>
                <span className="text-sm font-medium text-[#2C1810]/70 uppercase tracking-wider" data-id="element-243">
                  Americans with Alzheimer's
                </span>
              </motion.div>
              <div className="hidden md:block w-16 h-px bg-[#8B6914]/30" data-id="element-244"></div>
              <motion.div variants={fadeInUp} className="text-center md:text-left" data-id="element-245">
                <span className="block font-fraunces text-5xl md:text-7xl text-[#A0522D] mb-2" data-id="element-246">
                  11.3M
                </span>
                <span className="text-sm font-medium text-[#2C1810]/70 uppercase tracking-wider" data-id="element-247">
                  Unpaid Caregivers
                </span>
              </motion.div>
            </div>

            <motion.div variants={fadeInUp} className="md:col-span-7" data-id="element-248">
              <h2 className="font-fraunces text-3xl md:text-4xl mb-6 text-[#2C1810]" data-id="element-249">
                Why This Matters
              </h2>
              <p className="text-lg text-[#2C1810]/80 leading-relaxed mb-6" data-id="element-250">
                An estimated 7.4 million Americans are living with Alzheimer's —
                the most common form of dementia, and millions more with related
                memory diseases. In the United States, someone is diagnosed
                every 65 seconds.
              </p>
              <p className="text-lg text-[#2C1810]/80 leading-relaxed" data-id="element-251">
                Behind every diagnosis is a family navigating what comes next:
                roughly 11.3 million family members and unpaid caregivers
                provide over 16 billion hours of informal care each year. Our
                goal is to bring this conversation into the open — over coffee,
                in conversation, together, and to direct meaningful support to
                the organizations doing this work locally.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 3. EVENTS SECTION */}
      <section id="events" className="py-24 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto scroll-mt-12" data-id="element-252">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="text-center mb-16" data-id="element-253">
          <h2 className="font-fraunces text-4xl md:text-5xl text-[#2C1810] mb-4" data-id="element-254">
            The Lineup
          </h2>
          <p className="text-lg text-[#8B6914] font-medium max-w-2xl mx-auto" data-id="element-255">
            Every event, every mile, every inning goes toward awareness and
            support.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-256">
          {/* Card 1 */}
          <motion.article initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-white rounded-3xl p-8 md:p-10 shadow-sm shadow-[#2C1810]/5 flex flex-col h-full border border-[#FAF6F0]" data-id="element-257">
            <div className="flex justify-between items-start mb-6" data-id="element-258">
              <span className="bg-[#F5EFE6] text-[#A0522D] text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-259">
                Talk
              </span>
              <span className="bg-[#FAF6F0] text-[#2C1810] text-xs font-semibold px-3 py-1 rounded-full border border-[#EFE7D9]" data-id="element-260">
                Free
              </span>
            </div>
            <h3 className="font-fraunces text-2xl md:text-3xl text-[#2C1810] mb-4 leading-tight" data-id="element-261">
              Understanding Your Options
            </h3>
            <div className="flex flex-col gap-2 mb-6 text-sm text-[#8B6914] font-medium" data-id="element-262">
              <div className="flex items-center gap-2" data-id="element-263">
                <Calendar size={16} data-id="element-264" />
                <span data-id="element-265">Tuesday, June 2 · 7:00–8:30 PM</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-266">
                <MapPin size={16} data-id="element-267" />
                <span data-id="element-268">Grounded Coffee, Livermore</span>
              </div>
            </div>
            <p className="text-[#2C1810]/70 leading-relaxed mb-8 flex-grow" data-id="element-269">
              Join Delinda Brown, Director of Outreach & Admissions at Center
              for Elders' Independence, for an evening on the PACE program —
              comprehensive care that helps seniors live independently. Open Q&A
              and resource table to follow.
            </p>
            <a href="https://www.eventbrite.com/e/understanding-your-options-resources-for-at-risk-seniors-tickets-1989967900559" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#A0522D] text-white px-6 py-3 rounded-xl font-medium hover:bg-[#8B4513] transition-colors w-full sm:w-auto self-start" data-id="element-270">
              RSVP on Eventbrite
              <ExternalLink size={16} data-id="element-271" />
            </a>
          </motion.article>

          {/* Card 2 */}
          <motion.article initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="bg-white rounded-3xl p-8 md:p-10 shadow-sm shadow-[#2C1810]/5 flex flex-col h-full border border-[#FAF6F0]" data-id="element-272">
            <div className="flex justify-between items-start mb-6" data-id="element-273">
              <span className="bg-[#F5EFE6] text-[#A0522D] text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-274">
                Talk
              </span>
              <span className="bg-[#FAF6F0] text-[#2C1810] text-xs font-semibold px-3 py-1 rounded-full border border-[#EFE7D9]" data-id="element-275">
                Free
              </span>
            </div>
            <h3 className="font-fraunces text-2xl md:text-3xl text-[#2C1810] mb-4 leading-tight" data-id="element-276">
              Planning for What Matters
            </h3>
            <div className="flex flex-col gap-2 mb-6 text-sm text-[#8B6914] font-medium" data-id="element-277">
              <div className="flex items-center gap-2" data-id="element-278">
                <Calendar size={16} data-id="element-279" />
                <span data-id="element-280">Wednesday, June 3 · 6:00–8:00 PM</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-281">
                <MapPin size={16} data-id="element-282" />
                <span data-id="element-283">Grounded Coffee, Livermore</span>
              </div>
            </div>
            <p className="text-[#2C1810]/70 leading-relaxed mb-8 flex-grow" data-id="element-284">
              John Oliverus shares his firsthand experience navigating care for
              both parents, followed by estate planning attorney Kristina Park
              of Park Legacy Law Firm on trusts, wills, advance directives, and
              protecting your family before a crisis.
            </p>
            <a href="https://www.eventbrite.com/e/planning-for-what-matters-a-personal-story-estate-planning-tickets-1989968427134" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#A0522D] text-white px-6 py-3 rounded-xl font-medium hover:bg-[#8B4513] transition-colors w-full sm:w-auto self-start" data-id="element-285">
              RSVP on Eventbrite
              <ExternalLink size={16} data-id="element-286" />
            </a>
          </motion.article>

          {/* Card 3 */}
          <motion.article initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-white rounded-3xl p-8 md:p-10 shadow-sm shadow-[#2C1810]/5 flex flex-col h-full border border-[#FAF6F0]" data-id="element-287">
            <div className="flex justify-between items-start mb-6" data-id="element-288">
              <span className="bg-[#F5EFE6] text-[#A0522D] text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-289">
                Fundraiser
              </span>
              <span className="bg-[#FAF6F0] text-[#2C1810] text-xs font-semibold px-3 py-1 rounded-full border border-[#EFE7D9]" data-id="element-290">
                Ticketed
              </span>
            </div>
            <h3 className="font-fraunces text-2xl md:text-3xl text-[#2C1810] mb-4 leading-tight" data-id="element-291">
              Mahjong for Memory
            </h3>
            <div className="flex flex-col gap-2 mb-6 text-sm text-[#8B6914] font-medium" data-id="element-292">
              <div className="flex items-center gap-2" data-id="element-293">
                <Calendar size={16} data-id="element-294" />
                <span data-id="element-295">Thursday, June 4 · 7:00 PM</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-296">
                <MapPin size={16} data-id="element-297" />
                <span data-id="element-298">Good Morning Maxwell, Livermore</span>
              </div>
            </div>
            <p className="text-[#2C1810]/70 leading-relaxed mb-8 flex-grow" data-id="element-299">
              A night of learning and fun. Start with a guided Mahjong tutorial,
              then compete in a friendly tournament. No experience needed. All
              proceeds benefit Bay Area organizations supporting families
              affected by memory diseases.
            </p>
            <a href="https://www.eventbrite.com/e/1989968584605" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#A0522D] text-white px-6 py-3 rounded-xl font-medium hover:bg-[#8B4513] transition-colors w-full sm:w-auto self-start" data-id="element-300">
              Get Tickets on Eventbrite
              <ExternalLink size={16} data-id="element-301" />
            </a>
          </motion.article>

          {/* Card 4 */}
          <motion.article initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="bg-white rounded-3xl p-8 md:p-10 shadow-sm shadow-[#2C1810]/5 flex flex-col h-full border border-[#FAF6F0]" data-id="element-302">
            <div className="flex justify-between items-start mb-6" data-id="element-303">
              <span className="bg-[#F5EFE6] text-[#A0522D] text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-304">
                Challenge
              </span>
            </div>
            <h3 className="font-fraunces text-2xl md:text-3xl text-[#2C1810] mb-4 leading-tight" data-id="element-305">
              The 50-Mile Challenge
            </h3>
            <div className="flex flex-col gap-2 mb-6 text-sm text-[#8B6914] font-medium" data-id="element-306">
              <div className="flex items-center gap-2" data-id="element-307">
                <Calendar size={16} data-id="element-308" />
                <span data-id="element-309">All month — June 2026</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-310">
                <MapPin size={16} data-id="element-311" />
                <span data-id="element-312">Anywhere</span>
              </div>
            </div>
            <p className="text-[#2C1810]/70 leading-relaxed mb-8 flex-grow" data-id="element-313">
              Join our team for the Alzheimer's Association 50-Mile Challenge.
              Commit to 50 miles over the month of June — run, walk, or jog.
              Every mile raises funds and awareness. Sign up through the
              Alzheimer's Association and join the Grounded Coffee team.
            </p>
            <button disabled className="inline-flex items-center justify-center gap-2 bg-[#EFE7D9] text-[#2C1810]/50 px-6 py-3 rounded-xl font-medium cursor-not-allowed w-full sm:w-auto self-start" data-id="element-314">
              Join Our Team (Link Coming Soon)
            </button>
          </motion.article>

          {/* Card 5 - Spans full width on desktop for balance */}
          <motion.article initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="bg-white rounded-3xl p-8 md:p-10 shadow-sm shadow-[#2C1810]/5 flex flex-col md:flex-row gap-8 md:col-span-2 border border-[#FAF6F0]" data-id="element-315">
            <div className="flex-1" data-id="element-316">
              <div className="flex justify-between items-start mb-6" data-id="element-317">
                <span className="bg-[#F5EFE6] text-[#A0522D] text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-318">
                  Sports
                </span>
              </div>
              <h3 className="font-fraunces text-2xl md:text-3xl text-[#2C1810] mb-4 leading-tight" data-id="element-319">
                Coaches' Softball Tournament
              </h3>
              <div className="flex flex-col gap-2 mb-6 text-sm text-[#8B6914] font-medium" data-id="element-320">
                <div className="flex items-center gap-2" data-id="element-321">
                  <Calendar size={16} data-id="element-322" />
                  <span data-id="element-323">Date TBD</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-324">
                  <MapPin size={16} data-id="element-325" />
                  <span data-id="element-326">Livermore</span>
                </div>
              </div>
            </div>
            <div className="flex-1 flex flex-col" data-id="element-327">
              <p className="text-[#2C1810]/70 leading-relaxed mb-8 flex-grow" data-id="element-328">
                The coaches step off the sidelines and onto the field. Granada
                vs. Livermore Little League coaches square off in a charity
                softball game. Come cheer them on — all for a good cause.
              </p>
              <a href="https://forms.gle/q7eTRimBw2e2aA8j6" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#A0522D] text-white px-6 py-3 rounded-xl font-medium hover:bg-[#8B4513] transition-colors w-full sm:w-auto self-start" data-id="element-329">
                Sign Up to Play
                <ExternalLink size={16} data-id="element-330" />
              </a>
            </div>
          </motion.article>
        </div>
      </section>

      {/* 4. HOW TO GET INVOLVED */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#EFE7D9]" data-id="element-331">
        <div className="max-w-7xl mx-auto" data-id="element-332">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-333">
            <motion.div variants={fadeInUp} className="flex flex-col items-center" data-id="element-334">
              <div className="w-16 h-16 bg-[#FAF6F0] rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-335">
                <Users className="text-[#A0522D]" size={32} strokeWidth={1.5} data-id="element-336" />
              </div>
              <h3 className="font-fraunces text-2xl text-[#2C1810] mb-4" data-id="element-337">
                Attend
              </h3>
              <p className="text-[#2C1810]/70 leading-relaxed max-w-sm" data-id="element-338">
                Show up to an event. Every seat filled is a conversation
                started.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col items-center" data-id="element-339">
              <div className="w-16 h-16 bg-[#FAF6F0] rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-340">
                <Activity className="text-[#A0522D]" size={32} strokeWidth={1.5} data-id="element-341" />
              </div>
              <h3 className="font-fraunces text-2xl text-[#2C1810] mb-4" data-id="element-342">
                Run
              </h3>
              <p className="text-[#2C1810]/70 leading-relaxed max-w-sm" data-id="element-343">
                Join our 50-mile team or register for a future 5K. Every mile
                matters.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col items-center" data-id="element-344">
              <div className="w-16 h-16 bg-[#FAF6F0] rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-345">
                <Heart className="text-[#A0522D]" size={32} strokeWidth={1.5} data-id="element-346" />
              </div>
              <h3 className="font-fraunces text-2xl text-[#2C1810] mb-4" data-id="element-347">
                Share
              </h3>
              <p className="text-[#2C1810]/70 leading-relaxed max-w-sm" data-id="element-348">
                Tag a friend, share a post, spread the word. Awareness starts
                with a conversation.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 5. FOOTER */}
      <footer className="bg-[#2C1810] text-[#FAF6F0] py-16 px-6 md:px-12 lg:px-24" data-id="element-349">
        <div className="max-w-7xl mx-auto flex flex-col items-center text-center" data-id="element-350">
          <h2 className="font-fraunces text-3xl mb-8 text-[#FAF6F0]" data-id="element-351">
            Memory Care <span className="italic text-[#A0522D]" data-id="element-352">Together</span>
          </h2>

          <p className="text-[#FAF6F0]/80 mb-8 max-w-md leading-relaxed" data-id="element-353">
            Presented by Grounded Coffee
            <br data-id="element-354" />
            53 Wright Brothers Ave, Suite A<br data-id="element-355" />
            Livermore, CA 94551
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-6 mb-12" data-id="element-356">
            <a href="mailto:questions@staygrounded.app" className="flex items-center gap-2 text-[#FAF6F0]/80 hover:text-[#A0522D] transition-colors" data-id="element-357">
              <Mail size={18} data-id="element-358" />
              questions@staygrounded.app
            </a>
            <span className="hidden sm:block text-[#8B6914]" data-id="element-359">•</span>
            <a href="tel:9252920433" className="flex items-center gap-2 text-[#FAF6F0]/80 hover:text-[#A0522D] transition-colors" data-id="element-360">
              <Phone size={18} data-id="element-361" />
              (925) 292-0433
            </a>
            <span className="hidden sm:block text-[#8B6914]" data-id="element-362">•</span>
            <a href="https://instagram.com/groundedlivermore" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#FAF6F0]/80 hover:text-[#A0522D] transition-colors" data-id="element-363">
              <Instagram size={18} data-id="element-364" />
              @groundedlivermore
            </a>
          </div>

          <div className="w-full max-w-2xl h-px bg-[#FAF6F0]/10 mb-8" data-id="element-365"></div>

          <p className="text-sm text-[#8B6914] italic" data-id="element-366">
            100% of net proceeds benefit Bay Area organizations supporting
            families affected by Alzheimer's and dementia.
          </p>
        </div>
      </footer>
    </div>;
}